/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useRef } from 'react'
// Third-party
import { css } from '@emotion/react'
import { CloseOutlined, CalendarOutlined } from '@ant-design/icons'
import { Modal, DatePicker, Input } from 'antd'
import type { Dayjs } from 'dayjs'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
// Relative
import {
  heading4,
  paragraphSmall,
  paragraphMedium,
  weightSemiBold,
  weightMedium,
  weightRegular,
} from 'src/components/css'

interface BlackoutDaysModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (data: BlackoutDayFormData) => void
}

export interface BlackoutDayFormData {
  dates: Dayjs[]
  description: string
}

const modalStyles = css`
  .ant-modal-content {
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 0;
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    display: none;
  }
`

const headerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid ${colors.SECONDARY_100};
`

const titleStyles = css`
  ${heading4}
  ${weightSemiBold}
  color: ${colors.WHITE};
  margin: 0;
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
`

const closeButtonStyles = css`
  background: transparent;
  border: none;
  color: ${colors.WHITE};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`

const formStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  min-height: 400px;
`

const fieldContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`

const labelStyles = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.WHITE};
  margin: 0;
`

const inputBoxStyles = css`
  background-color: #313b4b;
  border: 1px solid #3a4659;
  border-radius: 8px;
  min-height: 40px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${paragraphMedium}
  ${weightRegular}
  color: ${colors.WHITE};

  &:hover {
    border-color: #3a4659;
  }

  &:focus,
  &:focus-within {
    border-color: ${colors.PRIMARY_300};
    box-shadow: none;
  }
`

const datePickerStyles = css`
  &.ant-picker {
    ${inputBoxStyles}
    width: 100%;

    .ant-picker-input {
      input {
        ${paragraphMedium}
        ${weightRegular}
        color: ${colors.WHITE};

        &::placeholder {
          color: ${colors.NEUTRAL_100};
        }
      }
    }

    .ant-picker-suffix {
      color: ${colors.WHITE};
      font-size: 18px;
    }

    .ant-picker-clear {
      background: #313b4b;
      color: ${colors.WHITE};
    }
  }
`

const textAreaStyles = css`
  &.ant-input {
    ${inputBoxStyles}
    min-height: 80px;
    resize: none;

    &::placeholder {
      color: ${colors.NEUTRAL_100};
    }
  }
`

const selectedDatesContainerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const selectedDatesListStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 150px;
  overflow-y: auto;
  padding: 12px;
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
`

const dateTagStyles = css`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background-color: ${colors.SECONDARY_200};
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 4px;
  color: ${colors.WHITE};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
`

const dateTagRemoveStyles = css`
  background: transparent;
  border: none;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.ERROR_300};
  }
`

const footerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-top: 1px solid ${colors.SECONDARY_100};
  gap: 16px;
`

const buttonContainerStyles = css`
  display: flex;
  gap: 16px;
  flex: 1;
`

const cancelButtonStyles = css`
  flex: 1;
  padding: 9px 25px;
  border-radius: 8px;
  border: 1px solid ${colors.WHITE};
  background: transparent;
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const confirmButtonStyles = css`
  flex: 1;
  padding: 8px 24px;
  border-radius: 8px;
  border: none;
  background: ${colors.PRIMARY_300};
  color: ${colors.WHITE};
  font-family: ${isCanlan
    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif'
    : 'BarlowCondensed, Arial, Helvetica, sans-serif'};
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY_400};
  }
`

const BlackoutDaysModal: React.FC<BlackoutDaysModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [selectedDates, setSelectedDates] = useState<Dayjs[]>([])
  const [description, setDescription] = useState('')
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const justSelectedDateRef = useRef(false)

  const resetForm = useCallback(() => {
    setSelectedDates([])
    setDescription('')
    setDatePickerOpen(false)
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleConfirm = useCallback(() => {
    if (selectedDates.length === 0) {
      return
    }
    onConfirm({
      dates: selectedDates,
      description,
    })
    resetForm()
  }, [onConfirm, selectedDates, description, resetForm])

  const handleDateChange = useCallback(
    (date: Dayjs | null) => {
      if (date) {
        // Mark that we just selected a date to prevent picker from closing
        justSelectedDateRef.current = true
        // Check if date already exists in the list
        const exists = selectedDates.some((d) => d.isSame(date, 'day'))
        if (!exists) {
          setSelectedDates((prev) => [...prev, date])
        }
      }
    },
    [selectedDates]
  )

  const handleOpenChange = useCallback((open: boolean) => {
    // If closing and we just selected a date, keep picker open
    if (!open && justSelectedDateRef.current) {
      justSelectedDateRef.current = false
      setDatePickerOpen(true)
      return
    }
    setDatePickerOpen(open)
  }, [])

  const handleRemoveDate = useCallback((dateToRemove: Dayjs) => {
    setSelectedDates((prev) =>
      prev.filter((d) => !d.isSame(dateToRemove, 'day'))
    )
  }, [])

  return (
    <Modal
      css={modalStyles}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      maskClosable={false}
      styles={{
        mask: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(6, 10, 16, 0.8)',
        },
      }}
    >
      {/* Header */}
      <div css={headerStyles}>
        <h2 css={titleStyles}>Add Blackout Days / Bye Week</h2>
        <button
          type='button'
          css={closeButtonStyles}
          onClick={handleClose}
          aria-label='Close modal'
        >
          <CloseOutlined />
        </button>
      </div>

      {/* Form */}
      <div css={formStyles}>
        {/* Date Picker */}
        <div css={fieldContainerStyles}>
          <label css={labelStyles}>Select Dates</label>
          <DatePicker
            css={datePickerStyles}
            value={null}
            onChange={handleDateChange}
            format='MMM DD, YYYY'
            placeholder='Click to add a date'
            suffixIcon={<CalendarOutlined />}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            open={datePickerOpen}
            onOpenChange={handleOpenChange}
          />
        </div>

        {/* Selected Dates Display */}
        {selectedDates.length > 0 && (
          <div css={selectedDatesContainerStyles}>
            <label css={labelStyles}>
              Selected Dates ({selectedDates.length})
            </label>
            <div css={selectedDatesListStyles}>
              {selectedDates
                .sort((a, b) => a.valueOf() - b.valueOf())
                .map((date) => (
                  <div key={date.format('YYYY-MM-DD')} css={dateTagStyles}>
                    <span>{date.format('MMM DD, YYYY')}</span>
                    <button
                      type='button'
                      css={dateTagRemoveStyles}
                      onClick={() => handleRemoveDate(date)}
                      aria-label={`Remove ${date.format('MMM DD, YYYY')}`}
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Description Row */}
        <div css={fieldContainerStyles}>
          <label css={labelStyles}>Description</label>
          <Input.TextArea
            css={textAreaStyles}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Enter description...'
            rows={2}
          />
        </div>
      </div>

      {/* Footer */}
      <div css={footerStyles}>
        <div css={buttonContainerStyles}>
          <button type='button' css={cancelButtonStyles} onClick={handleClose}>
            CANCEL
          </button>
          <button
            type='button'
            css={confirmButtonStyles}
            onClick={handleConfirm}
            disabled={selectedDates.length === 0}
          >
            CONFIRM
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default BlackoutDaysModal
